/**
 * Notices Domain
 * The notices that come over the page — a moderator's removal, the session's
 * end, a connection lost for good, a moderator's invitation — in one place:
 * which one is open. What the three that end play end is endPlay's
 * (domains/end-play.ts).
 *
 * One notice is drawn at a time, the first open in NOTICES' order: the three
 * that end play before the invitation, which ending play takes with it. Each
 * notice component opens on useOpenNotice, as the full-screen gate waits on
 * it: a notice added here is one the gate already waits for. Each store keeps
 * its own notice's words — when the removal ends, the world's name — and says
 * it is open as NOTICES reads it, and nowhere else.
 */

import { useSyncExternalStore } from 'react';

import { useBanNotificationStore } from './ban-notification';
import { useConnectionLostStore } from './connection-lost';
import { openInvitationSince, usePrivateVoiceChatStore } from './private-voice-chat';
import { useSessionEndedStore } from './session-ended';

/** A notice over the page. */
type Notice = 'removal' | 'sessionEnded' | 'connectionLost' | 'invitation';

/**
 * Each notice and whether it is open, highest first. A removal outranks the
 * session's end — it is the player's own, and its OK reloads — and the
 * session's end outranks a lost connection, whose Reconnect would only try to
 * join an ended session again. The invitation comes last: ending play closes
 * it (endPlay), so it is never under another.
 */
const NOTICES: ReadonlyArray<{ notice: Notice; open: () => boolean }> = [
  { notice: 'removal', open: () => useBanNotificationStore.getState().banData !== null },
  { notice: 'sessionEnded', open: () => useSessionEndedStore.getState().noticeOpen },
  { notice: 'connectionLost', open: () => useConnectionLostStore.getState().noticeOpen },
  { notice: 'invitation', open: () => openInvitationSince(usePrivateVoiceChatStore.getState()) !== null },
];

/** The stores NOTICES reads: a change in any can change which notice is open. */
const STORES = [useBanNotificationStore, useSessionEndedStore, useConnectionLostStore, usePrivateVoiceChatStore];

/** The notice over the page now, or null while none is. */
export function openNotice(): Notice | null {
  return NOTICES.find(({ open }) => open())?.notice ?? null;
}

function subscribe(onChange: () => void): () => void {
  const unsubscribes = STORES.map((store) => store.subscribe(onChange));
  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

/** The notice over the page, or null; re-renders as it changes. */
export function useOpenNotice(): Notice | null {
  return useSyncExternalStore(subscribe, openNotice);
}
